import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalPage } from "@/components/pages/LegalPage";
import { LEGAL_OWNER } from "@/lib/legal";

const TOC = [
  { id: "naturaleza", label: "Naturaleza de los productos" },
  { id: "marco", label: "Marco legal aplicable" },
  { id: "casos", label: "Casos que damos lugar" },
  { id: "sin-descarga", label: "Compras sin acceso habilitado" },
  { id: "tecnico", label: "Resolución técnica previa" },
  { id: "solicitud", label: "Cómo solicitarlo" },
  { id: "tramite", label: "Trámite y plazos" },
  { id: "acreditacion", label: "Acreditación del dinero" },
  { id: "diferencias", label: "Reembolso, contracargo y desconocimiento" },
];

export const Route = createFileRoute("/politica-de-reembolsos")({
  head: () => ({
    meta: [
      { title: "Política de reembolsos — Yustiz Haro Resources" },
      {
        name: "description",
        content:
          "Cómo se gestionan los reembolsos de productos digitales descargables en Yustiz Haro Resources: casos admitidos, trámite, plazos y revocación de acceso.",
      },
      { property: "og:title", content: "Política de reembolsos — Yustiz Haro Resources" },
      {
        property: "og:description",
        content: "Casos admitidos, trámite y plazos de reembolso de productos digitales.",
      },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recursos.yustizharo.com/politica-de-reembolsos" },
    ],
    links: [{ rel: "canonical", href: "https://recursos.yustizharo.com/politica-de-reembolsos" }],
  }),
  component: ReembolsosPage,
});

function ReembolsosPage() {
  return (
    <LegalPage
      title="Política de reembolsos"
      currentPath="/politica-de-reembolsos"
      toc={TOC}
      intro={
        <p>
          Esta política explica en qué casos {LEGAL_OWNER.brand} devuelve el importe de una compra
          y cómo se tramita cada solicitud. Podés iniciarla también desde el{" "}
          <Link to="/arrepentimiento" className="text-primary underline underline-offset-4">
            Botón de arrepentimiento
          </Link>
          .
        </p>
      }
    >
      <h2 id="naturaleza">1. Naturaleza de los productos</h2>
      <p>
        Todos los productos son ficheros informáticos digitales suministrados electrónicamente
        para su descarga y uso inmediato. No hay envío físico ni devolución de soporte material:
        una vez habilitado el acceso, el contenido queda íntegramente a disposición del comprador.
      </p>

      <h2 id="marco">2. Marco legal aplicable</h2>
      <p>
        El artículo 1116, inciso b, del Código Civil y Comercial de la Nación excluye del derecho
        de revocación el suministro de ficheros informáticos digitales suministrados por vía
        electrónica susceptibles de ser descargados o reproducidos con carácter inmediato para su
        uso permanente. Esa excepción se aplica una vez habilitado el acceso o realizada la
        descarga.
      </p>
      <p>
        Esta política no elimina ni restringe los derechos que la Ley 24.240 de Defensa del
        Consumidor y el Código Civil y Comercial reconocen de forma imperativa a los consumidores,
        incluidos los relativos a productos defectuosos o a operaciones no ejecutadas.
      </p>

      <h2 id="casos">3. Casos en los que se acepta y procesa el reembolso</h2>
      <ul>
        <li>Cobro duplicado del mismo pedido.</li>
        <li>Entrega de un producto distinto del adquirido.</li>
        <li>Archivos corruptos, incompletos o que no se corresponden con lo descrito.</li>
        <li>
          Imposibilidad técnica de descargar los archivos que no haya podido resolverse desde
          soporte.
        </li>
        <li>Pedido pagado y no entregado.</li>
        <li>Cualquier otro error atribuible a la plataforma.</li>
      </ul>

      <h2 id="sin-descarga">4. Compras sin acceso habilitado ni descargas realizadas</h2>
      <p>
        Si todavía no se habilitó el acceso al producto y no se descargó ningún archivo, la
        solicitud de cancelación se evalúa conforme a la normativa aplicable y, verificada esa
        situación, se procesa la devolución del importe abonado.
      </p>

      <h2 id="tecnico">5. Resolución técnica previa</h2>
      <p>
        Ante un problema de descarga o de integridad de los archivos, primero se intenta resolver
        la incidencia: se regeneran los enlaces de acceso, se verifica el estado del pedido y, si
        corresponde, se reemplazan los archivos afectados. Si el problema no puede resolverse, se
        procede con el reembolso.
      </p>

      <h2 id="solicitud">6. Cómo solicitar un reembolso</h2>
      <p>
        Escribí a <a href={`mailto:${LEGAL_OWNER.email}`}>{LEGAL_OWNER.email}</a>, usá la{" "}
        <Link to="/contacto">página de contacto</Link> o completá el{" "}
        <Link to="/arrepentimiento">Botón de arrepentimiento</Link>. Incluí siempre:
      </p>
      <ul>
        <li>Email utilizado en la compra.</li>
        <li>Identificador del pedido.</li>
        <li>Producto involucrado.</li>
        <li>Descripción del problema.</li>
      </ul>

      <h2 id="tramite">7. Trámite, confirmación y plazos</h2>
      <p>
        Toda solicitud recibe una confirmación de recepción con un número de caso. La solicitud se
        analiza junto con el estado del pedido, la habilitación del acceso y el registro de
        descargas, y se responde por email con la decisión y su fundamento. Si el reembolso se
        aprueba, se devuelve el importe por el mismo medio de pago utilizado en la compra y se
        revoca el acceso al producto, que deja de estar disponible en la biblioteca privada.
      </p>

      <h2 id="acreditacion">8. Acreditación del dinero</h2>
      <p>
        El tiempo de acreditación de la devolución depende de Mercado Pago, de PayPal y de la
        entidad financiera o el medio de pago utilizado. Una vez ordenada la devolución, esos
        plazos quedan fuera del control de {LEGAL_OWNER.brand}.
      </p>

      <h2 id="diferencias">9. Reembolso, contracargo y operación no reconocida</h2>
      <ul>
        <li>
          <strong>Reembolso:</strong> devolución gestionada directamente con la tienda, con
          revocación del acceso al producto.
        </li>
        <li>
          <strong>Contracargo:</strong> reclamo iniciado ante el emisor de la tarjeta o el
          procesador. Mientras dura el proceso, y si se resuelve a favor del reclamante, el acceso
          al producto se revoca automáticamente.
        </li>
        <li>
          <strong>Operación no reconocida:</strong> cargo que el titular del medio de pago afirma
          no haber realizado. Debe informarse de inmediato al procesador y a{" "}
          <a href={`mailto:${LEGAL_OWNER.email}`}>{LEGAL_OWNER.email}</a> para bloquear el acceso y
          colaborar con la investigación.
        </li>
      </ul>
      <p>
        Antes de iniciar un contracargo te pedimos contactarnos: la mayoría de los casos se
        resuelve directamente y en menos tiempo.
      </p>
    </LegalPage>
  );
}
